/**
 * The named labels rollout switch (ADR-039), on the Dev Console's Arche
 * Management page beside the app roles switch (ADR-034).
 *
 *   off    no workspace may create its FIRST named label
 *   pilot  only the listed workspaces may
 *   on     any org workspace may
 *
 * CREATION-ONLY: a workspace that has named labels keeps them, keeps them
 * enforced, and may make more whatever this says — turning it off must never
 * open a labelled document. Admitting more asks first; admitting less is one
 * click. The server reads off when the setting is missing or unreadable.
 *
 * Server state lives in React Query under
 * `queryKeys.adminArcheGovernance.vaultNamedLabelsRollout()`; the mutation
 * writes the server's answer back into that cache.
 */

import { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import api, {
  VAULT_NAMED_LABELS_PILOT_MAX,
  type VaultNamedLabelsRolloutDto,
  type VaultNamedLabelsRolloutMode,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const HOLDER_ID = /^vh_[a-f0-9]{32}$/;
const PILOT_MAX = VAULT_NAMED_LABELS_PILOT_MAX;

const MODES: Array<{ mode: VaultNamedLabelsRolloutMode; label: string; meaning: string }> = [
  {
    mode: 'off',
    label: 'Off',
    meaning:
      'No workspace can create its first named label. Workspaces that already have labels keep them, and they stay enforced.',
  },
  {
    mode: 'pilot',
    label: 'Pilot',
    meaning: 'Only the workspaces listed below can create their first named label.',
  },
  { mode: 'on', label: 'On', meaning: 'Any org workspace can create named labels.' },
];

const RANK: Record<VaultNamedLabelsRolloutMode, number> = { off: 0, pilot: 1, on: 2 };

/** Does `next` let a workspace create labels that `current` would not? */
function admitsMore(
  current: VaultNamedLabelsRolloutDto,
  next: VaultNamedLabelsRolloutDto,
): boolean {
  if (RANK[next.mode] !== RANK[current.mode]) return RANK[next.mode] > RANK[current.mode];
  if (next.mode !== 'pilot') return false;
  return next.pilotHolderIds.some((id) => !current.pilotHolderIds.includes(id));
}

function parsePilot(text: string): { ids: string[]; invalid: string[] } {
  const tokens = text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    ids: [...new Set(tokens.filter((t) => HOLDER_ID.test(t)))],
    invalid: tokens.filter((t) => !HOLDER_ID.test(t)),
  };
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function VaultNamedLabelsRolloutCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const rollout = useQuery({
    queryKey: queryKeys.adminArcheGovernance.vaultNamedLabelsRollout(),
    queryFn: () => api.adminGetVaultNamedLabelsRollout(),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (next: VaultNamedLabelsRolloutDto) => api.adminSetVaultNamedLabelsRollout(next),
    onSuccess: (result) => {
      qc.setQueryData(queryKeys.adminArcheGovernance.vaultNamedLabelsRollout(), result);
      toast({ title: `Named labels: ${result.mode}` });
    },
    onError: (err) =>
      toast({
        title: 'Could not change named labels',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  const [mode, setMode] = useState<VaultNamedLabelsRolloutMode>('off');
  const [pilotText, setPilotText] = useState('');
  const [confirming, setConfirming] = useState<VaultNamedLabelsRolloutDto | null>(null);
  const current = rollout.data;
  useEffect(() => {
    if (!current) return;
    setMode(current.mode);
    setPilotText(current.pilotHolderIds.join('\n'));
  }, [current]);

  const pilot = parsePilot(pilotText);
  const next: VaultNamedLabelsRolloutDto = { mode, pilotHolderIds: pilot.ids };
  const unchanged =
    !!current &&
    current.mode === next.mode &&
    current.pilotHolderIds.join(',') === next.pilotHolderIds.join(',');
  const blocked =
    pilot.invalid.length > 0 ||
    pilot.ids.length > PILOT_MAX ||
    (mode === 'pilot' && pilot.ids.length === 0);

  function submit(): void {
    if (!current) return;
    // Admitting more asks first; admitting less is one click.
    if (admitsMore(current, next)) setConfirming(next);
    else save.mutate(next);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Tag className="h-4 w-4" aria-hidden="true" />
          Named labels (ADR-039)
        </CardTitle>
        <CardDescription>
          Gates only a workspace’s FIRST named label. Turning it off never removes labels from a
          workspace that has them: its labelled documents stay protected and it can keep making
          labels. Off is the safe reading: the server treats a missing or unreadable setting as off.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {rollout.isError ? (
          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-destructive">
            <p>Could not load the named labels rollout: {errorMessage(rollout.error)}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => void rollout.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : !current ? (
          <div
            className="h-5 w-48 animate-pulse rounded bg-muted"
            aria-label="Loading named labels"
          />
        ) : (
          <>
            <p>
              Named labels are{' '}
              <span data-testid="named-labels-rollout-status" className="font-semibold">
                {MODES.find((m) => m.mode === current.mode)?.label}
              </span>
              {current.mode === 'pilot' && ` for ${current.pilotHolderIds.length} workspace(s)`}.
            </p>
            <div role="radiogroup" aria-label="Named labels rollout" className="flex gap-2">
              {MODES.map((m) => (
                <Button
                  key={m.mode}
                  role="radio"
                  aria-checked={mode === m.mode}
                  size="sm"
                  variant={mode === m.mode ? 'default' : 'outline'}
                  disabled={save.isPending}
                  onClick={() => setMode(m.mode)}
                >
                  {m.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {MODES.find((m) => m.mode === mode)?.meaning}
            </p>
            {mode === 'pilot' && (
              <div className="space-y-1">
                <Label htmlFor="named-labels-pilot">
                  Pilot workspaces (workspace ids, one per line)
                </Label>
                <Textarea
                  id="named-labels-pilot"
                  value={pilotText}
                  onChange={(e) => setPilotText(e.target.value)}
                  rows={4}
                  placeholder="vh_…"
                  disabled={save.isPending}
                />
                {pilot.invalid.length > 0 && (
                  <p className="text-xs text-destructive">
                    Not a workspace id: {pilot.invalid.slice(0, 3).join(', ')}
                  </p>
                )}
                {pilot.ids.length > PILOT_MAX && (
                  <p className="text-xs text-destructive">At most {PILOT_MAX} pilot workspaces.</p>
                )}
              </div>
            )}
            <Button size="sm" disabled={unchanged || blocked || save.isPending} onClick={submit}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Other servers pick a change up within about 30 seconds. Every change is audited.
            </p>
          </>
        )}
      </CardContent>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Let more workspaces create named labels?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.mode === 'on'
                ? 'Any org workspace will be able to create named labels.'
                : `The ${confirming?.pilotHolderIds.length ?? 0} listed workspace(s) will be able to create named labels.`}{' '}
              A workspace that creates labels keeps them: turning this off later stops new
              workspaces only, it never removes labels from one that has them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const value = confirming;
                setConfirming(null);
                if (value) save.mutate(value);
              }}
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
